import firebase from 'firebase/compat/app'
import { type FirebaseRemoteConfigKey } from 'root-constants'

type TAbTestVariantData = {
  value: string
  sendAnalytics: boolean
}

export type TRemoteConfigVariantData = Record<string, TAbTestVariantData>

const FALLBACK_VARIANT_DATA: TAbTestVariantData = {
  value: '',
  sendAnalytics: false,
}

const parseAbTestVariantData = (rawValue: string): TAbTestVariantData => {
  const trimmedValue = rawValue.trim()

  if (!trimmedValue) {
    return FALLBACK_VARIANT_DATA
  }

  try {
    const parsedValue: unknown = JSON.parse(trimmedValue)

    if (typeof parsedValue === 'string') {
      return parsedValue
        ? { value: parsedValue, sendAnalytics: true }
        : FALLBACK_VARIANT_DATA
    }

    if (
      typeof parsedValue !== 'object' ||
      parsedValue === null ||
      Array.isArray(parsedValue)
    ) {
      return FALLBACK_VARIANT_DATA
    }

    const { value, sendAnalytics } = parsedValue as {
      value?: unknown
      sendAnalytics?: unknown
    }

    if (typeof value !== 'string') {
      return FALLBACK_VARIANT_DATA
    }

    return {
      value,
      sendAnalytics: sendAnalytics === true,
    }
  } catch {
    const looksLikeJson =
      trimmedValue.startsWith('{') || trimmedValue.startsWith('[')

    return looksLikeJson
      ? FALLBACK_VARIANT_DATA
      : { value: trimmedValue, sendAnalytics: true }
  }
}

export const getRemoteConfigVariantData = ({
  firebaseRemoteConfig,
  firebaseRemoteConfigKey,
}: {
  firebaseRemoteConfig: firebase.remoteConfig.RemoteConfig | null
  firebaseRemoteConfigKey?:
    | FirebaseRemoteConfigKey
    | FirebaseRemoteConfigKey[]
    | null
}): TRemoteConfigVariantData => {
  if (!firebaseRemoteConfig || !firebaseRemoteConfigKey) {
    return {}
  }

  const keys = Array.isArray(firebaseRemoteConfigKey)
    ? firebaseRemoteConfigKey
    : [firebaseRemoteConfigKey]

  return Object.fromEntries(
    keys.map((key) => [
      key,
      parseAbTestVariantData(firebaseRemoteConfig.getString(key)),
    ]),
  )
}
